import {
    HttpClient,
    HttpErrorResponse,
    HttpEvent,
    HttpEventType,
    HttpProgressEvent,
    HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { filter, finalize, map, tap } from 'rxjs/operators';

import {
    INgxFileUploadRequest,
    INgxFileUploadRequestData,
    NgxFileUploadState,
    NgxFileUploadStorage
} from '@ngx-file-upload/core';
import { MediahavenFileUploadFactory } from '../../helpers/file-upload-factory';

export class PendingRequest<T> {
    url: string;
    data: any;
    subscription$: Subject<T>;
    cancel$: Subject<void>;

    constructor(url: string, data: any, subscription$: Subject<T>, cancel$: Subject<void>) {
        this.url = url;
        this.data = data;
        this.subscription$ = subscription$;
        this.cancel$ = cancel$;
    }
}

@Injectable({ providedIn: 'root' })
export class UploadRequestQueue {
    private readonly MAX_CONCURRENCY: number = 5;
    private queue: NgxFileUploadStorage;

    private requestFactory: MediahavenFileUploadFactory;

    constructor(private httpClient: HttpClient) {
        this.requestFactory = new MediahavenFileUploadFactory(httpClient);
        this.queue = new NgxFileUploadStorage({ concurrentUploads: this.MAX_CONCURRENCY, autoStart: true });
    }

    /** Call this method to add your http request to queue */
    invoke<T>(url: string, data: any): Observable<HttpEvent<T>> {
        return this.addRequestToQueue<T>(url, data);
    }

    private addRequestToQueue<T>(url: string, data: FormData): Observable<HttpEvent<T>> {
        const sub$: Subject<HttpEvent<T>> = new Subject<HttpEvent<T>>();

        const request: INgxFileUploadRequest = this.requestFactory.createMediahavenUploadRequest({ url }, data);
        this.queue.add(request);

        request.change
            .pipe(
                // Don't handle INVALID, CANCELED, IDLE and PENDING
                filter(() => request.state > 3),
                map((requestData: INgxFileUploadRequestData) => {
                    if (requestData.hasError) {
                        const error: HttpErrorResponse = requestData.response as HttpErrorResponse;
                        return error;
                    }

                    switch (requestData.state) {
                        case NgxFileUploadState.START: {
                            const startEvent: HttpProgressEvent = {
                                type: HttpEventType.UploadProgress,
                                loaded: 0,
                                total: requestData.size
                            };
                            return startEvent;
                        }

                        case NgxFileUploadState.PROGRESS: {
                            const progressEvent: HttpProgressEvent = {
                                type: HttpEventType.UploadProgress,
                                loaded: requestData.uploaded,
                                total: requestData.size
                            };
                            return progressEvent;
                        }

                        case NgxFileUploadState.COMPLETED: {
                            return new HttpResponse<T>({
                                body: (requestData.response as HttpResponse<T>).body,
                                status: 200
                            });
                        }

                        default: {
                            return new HttpResponse<T>();
                        }
                    }
                })
            )
            .subscribe(response => sub$.next(response as any));

        const obs$: Observable<HttpEvent<T>> = sub$.pipe(
            // Since the library catches the httpError, we need to manually throw it
            tap(event => {
                if (Object.prototype.hasOwnProperty.call(event, 'status')) {
                    const response: HttpResponse<T> = event as HttpResponse<T>;
                    if (response.status >= 400) {
                        throw response;
                    }
                }
            }),
            finalize(() => {
                request.cancel();
                this.queue.remove(request);
            })
        );

        return obs$;
    }
}
